import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import AttendanceEmployeeFilter from "../../../components/AttendanceEmployeeFilter";
import moment from "moment";
import ReportingSelect from "../../../components/ReportingSelect";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";



const AttendanceEmployeeMonthly = () => {
  // Default values from session storage
  const defaultEmployeeId = localStorage.getItem("defaultEmployeeId");
  const defaultEmployeeLabel = localStorage.getItem("defaultEmployeeLabel") || "Default Employee";
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  // Initialize state
  const [data, setData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(
    defaultEmployeeId ? { value: defaultEmployeeId, label: defaultEmployeeLabel } : null
  );
  const [selectedDate, setSelectedDate] = useState(moment());
  const dateFormat = localStorage.getItem("dateFormat") || "MMM DD, YYYY";
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(parseInt(localStorage.getItem("empid")) || null);
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState(localStorage.getItem("empName") || null);
  const cdnUrl = process.env.REACT_APP_CDN_URL;

  const holidays = ['2024-05-22', '2024-08-15'];
  const weekends = [0, 6];

  // Fullscreen toggle logic
  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  // Fetch attendance data
  const fetchData = async (empId, date) => {
    try {
      setData([]);
      const response = await axios.get(`${apiUrl}/attendance/list?empid=${empId}&month=${date.month() + 1}&year=${date.year()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const transformedData = response.data.map((attendance) => {
       // const totalHours = parseFloat(attendance.total_hours) || 0;
        const workingHours = 9;
        //const deviation = workingHours - totalHours;

        const attendanceDate = moment(attendance.attendance_date);
        const isHoliday = holidays.includes(attendanceDate.format("YYYY-MM-DD"));
        const isWeekend = weekends.includes(attendanceDate.day());

        let attendanceStatus = attendance.attendance_status;
        if (isHoliday) {
          attendanceStatus = "Holiday";
        } else if (isWeekend) {
          attendanceStatus = "Weekend";
        }

        return {
          key: attendance.id,
          id: attendance.id,
          name: attendance.name,
          email: attendance.email,
          empcode: attendance.empcode,
          attendance_date: attendanceDate.format(dateFormat),
          attendance_status: attendanceStatus,
          check_in_time: attendance.check_in_time,
          check_out_time: attendance.check_out_time,
          total_hours: attendance.total_hours,
          working_hours: workingHours.toFixed(2),
          deviation: attendance.deviation,
          leave_status: attendance.leave_status,
          isHoliday,
          isWeekend,
        };
      });
      setData(transformedData);
      setLoading(false);

    } catch (error) {
      console.error("Error fetching attendance data:", error);
    }
  };


  const formatTimeFromDecimal = (decimalTime) => {
    const hours = Math.floor(Math.abs(decimalTime));
    const minutes = Math.round((Math.abs(decimalTime) - hours) * 60);
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes} Hrs`;
  };
  


  // Trigger data fetch when selectedEmployee or selectedDate changes
  useEffect(() => {
    if (selectedEmployeeId) {
      fetchData(selectedEmployeeId, selectedDate);
    }
  }, [selectedEmployeeId, selectedDate]);

  // Handle employee change
  const handleEmployeeChange = (selectedOption) => {
    if (selectedOption) {
      const { value, label } = selectedOption;
      setSelectedEmployee({ value, label });
      localStorage.setItem("defaultEmployeeId", value);
      localStorage.setItem("defaultEmployeeLabel", label);
    } else {
      setSelectedEmployee(null);
      localStorage.removeItem("defaultEmployeeId");
      localStorage.removeItem("defaultEmployeeLabel");
    }

    setSelectedEmployeeId(selectedOption.value); // Set the selected employee ID
    setSelectedEmployeeLabel(selectedOption.label); // Set the selected employee label

  };

  // Handle previous month
  const handlePrevMonth = () => {
    setSelectedDate(selectedDate.clone().subtract(1, 'month'));
  };

  // Handle next month
  const handleNextMonth = () => {
    setSelectedDate(selectedDate.clone().add(1, 'month'));
  };

  // Define table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "attendance_date",
      sorter: (a, b) => new Date(a.attendance_date) - new Date(b.attendance_date),
    },
    {
      title: "Check-In Time",
      dataIndex: "check_in_time",
      sorter: (a, b) => a.check_in_time.localeCompare(b.check_in_time),
    },
    {
      title: "Check-Out Time",
      dataIndex: "check_out_time",
      sorter: (a, b) => a.check_out_time.localeCompare(b.check_out_time),
    },
    {
      title: "Total Hours",
      dataIndex: "total_hours",
      sorter: (a, b) => a.total_hours - b.total_hours,
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      sorter: (a, b) => a.working_hours - b.working_hours,
    },

  {
    title: "Deviation",
    dataIndex: "deviation",
    sorter: (a, b) => a.deviation - b.deviation,
    render: (text, record) => {
      const deviation = parseFloat(record.deviation); // Ensure deviation is a number
      const formattedTime = record.deviation;
  
      if (deviation > 0) {
        // Positive deviation, show in red
        return <span style={{ color: 'red' }}>{formattedTime}</span>;
      } else if (deviation < 0) {
        // Negative deviation, show in green
        return <span style={{ color: 'green' }}>{formattedTime}</span>;
      } else {
        // Zero deviation
        return <span>{formattedTime}</span>;
      }
    },
  },
  

    
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a, b) => {
        const statusA = a.leave_status || a.attendance_status || "";
        const statusB = b.leave_status || b.attendance_status || "";
        return statusA.localeCompare(statusB);
      },
      render: (text, record) => {
        return record.leave_status ? record.leave_status : record.attendance_status;
      },
    },
  ];

  // Apply class names based on row conditions
  const rowClassName = (record) => {
    if (record.isHoliday) return "holiday-row";
    if (record.isWeekend) return "weekend-row";
    return "";
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
      <div className="page-header page-top-icons">
      <div className="row align-items-center">
            <div className="col-md-4">
             
                                          <ReportingSelect  name="employee"
  value={selectedEmployeeId ? { value: selectedEmployeeId, label: selectedEmployeeLabel } : null}
  onChange={handleEmployeeChange}
/>
            </div>

            <div className="col-md-4">
              <Button className="fa fa-chevron-circle-left" onClick={handlePrevMonth} />
              <span style={{ margin: '0 10px', fontWeight: 'bold', fontSize: '16px' }}>
                {selectedDate.format('MMMM YYYY')}
              </span>
              <Button className="fa fa-chevron-circle-right" onClick={handleNextMonth} />
            </div>

            <div className="col-md-4 float-end ms-auto">
              <div className="d-flex title-head">
              <div className="view-icons">
                    <Link to="/attendance-weekly" className="grid-view btn btn-link">
                      <i className="fa fa-th" />
                    </Link>
                    <Link to="/attendance-employee" className="grid-view btn btn-link">
                     
                    <i class="fa fa-calendar-check"></i>      
                    </Link>

                    <Link to="/attendance-monthly" className="list-view btn btn-link active mx-2">
                      <i className="fa fa-bars" />
                    </Link>

                    <Link to="#" className="grid-view btn btn-link">
                      <i className="las la-redo-alt" />
                    </Link>
                    <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                  </div>
             
              </div>
            </div>
          </div>
        </div>

        <div className="row"></div>

        <div className="row">
          <div className="col-lg-12">
            {loading ? ( // Conditionally render loader
              <div id="loader-wrapper-">
                <div className="loader">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={data}
                  className="table-striped"
                  rowClassName={rowClassName}
                  rowKey={(record) => record.id}
                  pagination={{ pageSize: 25 }}  // Set default page size to 25
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceEmployeeMonthly;
